/**
 * Tooltip — Base UI Tooltip primitive.
 *
 * Design system mapping:
 *   • Popup: bg-webex-gray-1000 text-white (light) / dark: bg-white text-webex-gray-1000
 *   • Padding: px-2 py-1 text-xs rounded-md
 *   • Arrow: matches popup color, 8px
 *
 * Wrap your app (or section) in <TooltipProvider> to enable hover delay
 * grouping. Without it tooltips still work but each manages its own delay.
 *
 * Usage:
 *   <TooltipProvider>
 *     <Tooltip content="Help text">
 *       <Button>Hover me</Button>
 *     </Tooltip>
 *   </TooltipProvider>
 */
import * as React from "react"
import { Tooltip as BaseTooltip } from "@base-ui-components/react/tooltip"
import { cn } from "@lib/cn"

export const TooltipProvider = BaseTooltip.Provider

export interface TooltipProps {
  content: React.ReactNode
  children: React.ReactElement
  side?: "top" | "right" | "bottom" | "left"
  align?: "start" | "center" | "end"
  sideOffset?: number
  className?: string
  multiline?: boolean
  open?: boolean
  defaultOpen?: boolean
  onOpenChange?: (open: boolean) => void
}

export function Tooltip({
  content,
  children,
  side = "top",
  align = "center",
  sideOffset = 6,
  className,
  multiline,
  open,
  defaultOpen,
  onOpenChange,
}: TooltipProps) {
  return (
    <BaseTooltip.Root open={open} defaultOpen={defaultOpen} onOpenChange={onOpenChange}>
      <BaseTooltip.Trigger render={children as React.ReactElement<Record<string, unknown>>} />
      <BaseTooltip.Portal>
        <BaseTooltip.Positioner side={side} align={align} sideOffset={sideOffset} className="z-50">
          <BaseTooltip.Popup
            className={cn(
              "rounded-md px-2 py-1 text-xs leading-tight",
              "bg-webex-gray-1000 text-white dark:bg-white dark:text-webex-gray-1000",
              "shadow-[0_4px_12px_rgba(0,0,0,0.18)]",
              "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
              "transition-opacity duration-150 motion-reduce:transition-none",
              multiline ? "max-w-56 whitespace-normal" : "whitespace-nowrap",
              className,
            )}
          >
            {content}
          </BaseTooltip.Popup>
        </BaseTooltip.Positioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  )
}
